import React, { useState, useEffect } from 'react';
import envConfig from '@nile/home/src/Config/env';
import {
  AIActivator,
  getTranslatedText,
  useIntl,
  loadGifDurations
} from '@nile/components';
import { BubbleText } from '../config/onboardingConfig';
import { Button, DisplayText } from '@superops/ui-design-system';
import { renderActionButtons } from './buttonRenderer';
import { FormattedMessage } from 'react-intl';

interface SwitchableStepProps {
  stepIndex?: number;
  hiddenButtons: Map<number, Set<number>>;
  apiLoading: boolean;
  showSwitchableStep: boolean;
  onboardingConfig: any;
  onButtonAction: (buttonConfig: any, index: number, stepIndex: number) => void;
  onGifLoad?: () => void;
}

const SwitchableStep: React.FC<SwitchableStepProps> = ({
  stepIndex = 0,
  hiddenButtons,
  apiLoading,
  showSwitchableStep,
  onboardingConfig,
  onButtonAction,
  onGifLoad
}) => {
  const intl = useIntl();

  const [selectedFeature, setSelectedFeature] = useState(0);
  const [gifDurations, setGifDurations] = useState<Map<number, number>>(
    new Map()
  );
  const [animationKey, setAnimationKey] = useState(0);
  const config = onboardingConfig;

  if (!config || !config.customSteps || config.customSteps.length === 0)
    return null;

  const currentStep = config.customSteps[stepIndex] as any;
  if (!currentStep || !currentStep.data || currentStep.data.length === 0)
    return null;

  // Handle nested-choice type
  if (currentStep.type === 'nested-choice') {
    return (
      <>
        <div className="ai-nested-choice-container">
          <div className="nested-choices-wrapper mb20">
            {currentStep.data.map((choice, index) => (
              <div
                key={index}
                className="nested-choice-item p10 mb10 border-1 border-gray-300 rounded cursor-pointer hover:bg-gray-100"
                onClick={() => {
                  // Handle choice selection if needed
                  console.log('Selected choice:', choice);
                }}
              >
                {choice}
              </div>
            ))}
          </div>
          {renderActionButtons({
            stepIndex,
            config,
            hiddenButtons,
            onButtonAction
          })}
        </div>
      </>
    );
  }

  // Handle feature-showcase type
  if (currentStep.type !== 'feature-showcase') return null;

  const features = currentStep.data;
  const autoSwitchTimeout = currentStep.timeoutDelay || 3000; // Default from config

  // Load GIF durations when component mounts or features change
  // useEffect(() => {
  //   const loadDurations = async () => {
  //     if (features && features.length > 0) {
  //       // Only load durations for features that have gifs
  //       const featuresWithGifs = features.filter((f) => f?.gif);
  //       if (featuresWithGifs.length > 0) {
  //         const baseUrl = `${envConfig.imageRoot}gifs/Onboarding/`;
  //         const durations = await loadGifDurations(featuresWithGifs, baseUrl);
  //         setGifDurations(durations);
  //       }
  //     }
  //   };

  //   loadDurations();
  // }, [features]);

  // Restart animation when GIF loops
  useEffect(() => {
    if (!showSwitchableStep || apiLoading) return;

    const currentDuration =
      gifDurations.get(selectedFeature) || autoSwitchTimeout;

    // Increment animation key to force animation restart in sync with GIF
    const animationInterval = setInterval(() => {
      setAnimationKey((prev) => prev + 1);
    }, currentDuration);

    return () => clearInterval(animationInterval);
  }, [
    selectedFeature,
    gifDurations,
    autoSwitchTimeout,
    showSwitchableStep,
    apiLoading
  ]);

  // Auto-switching logic for this specific step
  useEffect(() => {
    if (!showSwitchableStep || apiLoading) return;

    // Use the actual GIF duration for the current feature, fallback to autoSwitchTimeout
    const currentDuration =
      gifDurations.get(selectedFeature) || autoSwitchTimeout;

    const interval = setInterval(() => {
      setSelectedFeature((prev) => {
        const featuresWithGif = features
          .map((feature, idx) => (feature?.gif ? idx : -1))
          .filter((idx) => idx !== -1);

        if (featuresWithGif.length === 0) return prev;

        const currentPos = featuresWithGif.indexOf(prev);
        const nextPos = (currentPos + 1) % featuresWithGif.length;

        return featuresWithGif[nextPos];
      });
    }, currentDuration);

    return () => clearInterval(interval);
  }, [
    features.length,
    autoSwitchTimeout,
    showSwitchableStep,
    apiLoading,
    selectedFeature,
    gifDurations
  ]);

  const featureWrapperClass = [
    'summary-type-wrapper min-width-100',
    apiLoading && 'cursor-not-allowed'
  ]
    .filter(Boolean)
    .join(' ');

  const changeFeature = (featureIndex) => {
    if (!apiLoading) {
      setSelectedFeature(featureIndex);
    }
  };
  const urlInfo = features[selectedFeature].gif;
  const imageUrl = `${envConfig.imageRoot}svg/OnboardingWidget/${urlInfo}`;
  return (
    <div className="ai-features-container pb50">
      {/* Feature selection tabs */}
      <div className="mb10">
        <DisplayText element="p" size="large" variant="subdued">
          <FormattedMessage id="ai.onboarding.common.whatyouget" />
        </DisplayText>
      </div>

      <div className="feature-selection-tabs p10 g20 br10 mb20 flex col ">
        {features.map((feature, index) => (
          <div
            key={index}
            className={`feature-tab`}
            onClick={() => changeFeature(index)}
          >
            ✅ <FormattedMessage id={feature.title} />
          </div>
        ))}
      </div>
      <div className="mb10">
        <DisplayText element="p" size="large" variant="subdued">
          <FormattedMessage id="admin.CustomFields.h2.preview" />
        </DisplayText>
      </div>

      <div className="mb10 flex g10">
        {features.map((feature, index) => {
          if (feature?.gif) {
            const featureTypeClass = [
              'ai-onboarding-type p2 pl10 pr10 br20 mb12 pointer white-color fs10 w-max-content min-w100',
              index === selectedFeature && 'active',
              apiLoading && 'pointer-events-none'
            ]
              .filter(Boolean)
              .join(' ');

            return (
              <div key={`${feature?.gif}`} className={featureWrapperClass}>
                <div
                  key={`${feature?.gif}`}
                  className={featureTypeClass}
                  style={{
                    animationDuration:
                      index === selectedFeature
                        ? `${gifDurations.get(index) || autoSwitchTimeout}ms`
                        : undefined
                  }}
                  onClick={() => changeFeature(index)}
                  role="button"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      changeFeature(index);
                    }
                  }}
                >
                  {/* {feature.title} */}
                </div>
              </div>
            );
          }
          return <></>;
        })}
      </div>
      {/* Display selected feature content */}
      {features[selectedFeature] && features[selectedFeature].gif && (
        <div className="selected-feature-content min-height-300">
          <div className="feature-gif-container mt10">
            <img
              src={imageUrl}
              alt={getTranslatedText(intl, 'common.label.loading')}
              className="feature-demo-gif fw"
              // onLoad={onGifLoad}
            />
          </div>
        </div>
      )}
      {onboardingConfig.additionalNote && (
        <div className="mt10">
          <DisplayText size="medium" variant="subdued">
            <FormattedMessage id={onboardingConfig.additionalNote} />
          </DisplayText>
        </div>
      )}

      {renderActionButtons({
        stepIndex,
        config,
        hiddenButtons,
        onButtonAction
      })}
    </div>
  );
};

export default SwitchableStep;
